import { ChangeEvent, FC, useState } from 'react';
import { Box, Chip, Skeleton, Text } from '@hopskipdrive/react-components';

import { Card } from '../../../../../shared/components/Card';
import { Pagination } from '../../../../../shared/components/Pagination';
import { colorPalette, colorTokens } from '../../../../../shared/components/variables';
import { formatInTimezone } from '../../../../../shared/utils/helpers/date';
import { estimatedTimeFromNow } from '../../../../../shared/utils/helpers/util';
import { useEventsQuery } from '../../../../hooks/useEventsQuery';
import { EventListItem } from '../../../../types/events';
import { ContentHeading } from '../ContentHeading';

import { DotIcon, EventContainer, InnerCard } from './Events.styles';

interface EventsProps {
  rideSlug: string;
  timeZone: string;
}

const EVENTS_QUERY_PARAMS = {
  page: 1,
  perPage: 5,
};

export const Events: FC<EventsProps> = ({ rideSlug, timeZone }) => {
  const [pageNumber, setPageNumber] = useState<number>(EVENTS_QUERY_PARAMS.page);
  const { isFetching, data: eventsResponse } = useEventsQuery({
    rideSlug: rideSlug,
    queryParams: { page: pageNumber, perPage: EVENTS_QUERY_PARAMS.perPage },
  });

  const eventCount = eventsResponse?.meta?.totalCount || 0;
  const perPage = eventsResponse?.meta?.perPage || 1;
  const pageCount = Math.ceil(eventCount / perPage);

  return (
    <Card>
      <ContentHeading icon="timeline-rounded" headingTitle="Events" />
      <InnerCard>
        <ListOfEvents
          events={eventsResponse?.data}
          pageCount={pageCount}
          isFetching={isFetching}
          onPageChange={(_, page) => setPageNumber(page)}
          timeZone={timeZone}
        />
      </InnerCard>
    </Card>
  );
};

interface ListOfEventsProps {
  isFetching: boolean;
  events: EventListItem[];
  pageCount: number;
  onPageChange: (event: ChangeEvent<unknown>, page: number) => void;
  timeZone: string;
}

// To facilitate testing, we can export the unconnected component as well
export const ListOfEvents: FC<ListOfEventsProps> = ({ isFetching, events, pageCount, onPageChange, timeZone }) => {
  return (
    <Box display="flex" flexDirection="column" gap="10px">
      {isFetching ? (
        <SkeletonEvents />
      ) : (
        events?.map((event, index) => (
          <EventContainer key={index}>
            <Box display="flex" flexDirection="row" gap="6px" alignItems="center" width="100%">
              <DotIcon />
              <Text variant="subtitle2" textColor="primaryText">
                {formatInTimezone(event.createdAt, timeZone, "EEE, M/dd/yyyy 'at' h:mm aa zzz ")}
                {estimatedTimeFromNow(event.createdAt)}
              </Text>
              <Chip
                type="square"
                label={event.title}
                style={{
                  border: 0,
                  backgroundColor: colorPalette.neutral650,
                  padding: '2px 12px',
                  color: colorTokens.text.secondary,
                  fontSize: '12px',
                }}
              />
            </Box>
            <Text variant="body2" fontWeight={300} textColor="secondaryText" ml="16px">
              {event.description}
            </Text>
          </EventContainer>
        ))
      )}
      {pageCount > 1 && <Pagination count={pageCount} siblingCount={3} onChange={onPageChange} />}
    </Box>
  );
};

export const SkeletonEvents = () => (
  <>
    <EventContainer>
      <Skeleton data-testid="events-skeleton" variant="text" width={400} height={24} />
    </EventContainer>
    <EventContainer>
      <Skeleton data-testid="events-skeleton" variant="text" width={400} height={24} />
    </EventContainer>
    <EventContainer>
      <Skeleton data-testid="events-skeleton" variant="text" width={400} height={24} />
    </EventContainer>
    <EventContainer>
      <Skeleton data-testid="events-skeleton" variant="text" width={400} height={24} />
    </EventContainer>
    <EventContainer>
      <Skeleton data-testid="events-skeleton" variant="text" width={400} height={24} />
    </EventContainer>
  </>
);
